import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, Button, Flex, HStack, Text, VStack } from '@bonusly/design-system';

import { useSlackAuthorize, useSlackDisconnect, useSlackStatus } from '../../queries/slack';
import { SlackLogo } from './slack-logo';

interface SlackConnectCardProps {
	returnPath?: string;
}

export const SlackConnectCard: FC<SlackConnectCardProps> = ({ returnPath }) => {
	const { t } = useTranslation('bizy_history', { keyPrefix: 'connectors' });
	const { data: status, isLoading: isLoadingStatus } = useSlackStatus();
	const { mutateAsync: authorize, isLoading: isAuthorizing } = useSlackAuthorize();
	const { mutateAsync: disconnect, isLoading: isDisconnecting } = useSlackDisconnect();
	const { track } = useAnalytics();

	const connectionStatus = status?.status;

	useEffect(() => {
		if (connectionStatus === 'connected' && sessionStorage.getItem('bizy_connecting_slack')) {
			track(AnalyticsEvent.Slack.Connected);
			sessionStorage.removeItem('bizy_connecting_slack');
		}
	}, [connectionStatus, track]);

	const handleConnect = useCallback(async () => {
		track(AnalyticsEvent.Slack.ConnectClicked);
		try {
			const { redirectUrl } = await authorize(returnPath ?? window.location.pathname);
			if (redirectUrl) {
				sessionStorage.setItem('bizy_connecting_slack', 'true');
				window.location.href = redirectUrl;
			}
		} catch {
			// Error handled by React Query
		}
	}, [authorize, returnPath, track]);

	const handleDisconnect = useCallback(async () => {
		try {
			await disconnect();
			track(AnalyticsEvent.Slack.Disconnected);
		} catch {
			// Error handled by React Query
		}
	}, [disconnect, track]);

	if (isLoadingStatus) return null;
	const isConnected = connectionStatus === 'connected';

	return (
		<Box py={3} borderBottom="1px solid" borderColor="border.soft">
			<Flex alignItems="center" justifyContent="space-between">
				<HStack spacing={3} align="center">
					<Box color="text.default" flexShrink={0} display="inline-flex" alignItems="center">
						<SlackLogo width="24" height="24" />
					</Box>
					<VStack spacing={0} align="flex-start">
						<Text size="md" variant="strong" color="text.default">
							{t('slack.name')}
						</Text>
						<Text size="sm" color="text.softer">
							{isConnected ? t('connected') : t('not_connected')}
						</Text>
					</VStack>
				</HStack>
				{isConnected ? (
					<Button size="md" variant="ghost" colorScheme="secondary" onClick={handleDisconnect} isLoading={isDisconnecting}>
						{t('disconnect')}
					</Button>
				) : (
					<Button size="md" colorScheme="primary" onClick={handleConnect} isLoading={isAuthorizing}>
						{t('connect')}
					</Button>
				)}
			</Flex>
		</Box>
	);
};
